import type {
  DiscoveredOrganization,
  useStytchB2BClient
} from '@stytch/react/b2b'
import { match } from 'ts-pattern'

import { isExistingMembership, needsPrimaryAuth } from 'lib/auth/organizations'

export type DiscoveryTokenType = 'discovery' | 'discovery_oauth'

type StytchB2BClient = ReturnType<typeof useStytchB2BClient>

export const discoveryFlowFor = (
  stytch: StytchB2BClient,
  tokenType: DiscoveryTokenType
) =>
  match(tokenType)
    .with('discovery_oauth', () => ({
      authenticate: (token: string) =>
        stytch.oauth.discovery.authenticate({ discovery_oauth_token: token }),
      canSignInTo: (org: DiscoveredOrganization) =>
        !needsPrimaryAuth(org) && isExistingMembership(org)
    }))
    .with('discovery', () => ({
      authenticate: (token: string) =>
        stytch.magicLinks.discovery.authenticate({
          discovery_magic_links_token: token
        }),
      canSignInTo: (org: DiscoveredOrganization) => !needsPrimaryAuth(org)
    }))
    .exhaustive()

export const toWorkspaces = (organizations: DiscoveredOrganization[]) =>
  organizations
    .map(org => ({
      id: org.organization.organization_id,
      name: org.organization.organization_name
    }))
    .sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    )

/** Stytch returns it on OAuth discovery, though the SDK type omits it. */
export const oauthProviderOf = (response: object) =>
  'provider_type' in response && typeof response.provider_type === 'string'
    ? response.provider_type.toLowerCase()
    : null
